/**
 * Find contacts — plan §17.5.
 *
 * The panel exists to make one distinction impossible to miss: **which clicks
 * spend money.** That depends on the vendor: `pricing.search` and
 * `pricing.reveal` say so, and a billed search is estimated and confirmed
 * first, the way a reveal always has been. Every affordance here is arranged
 * around which click costs money.
 *
 *   Search   → a preview. No addresses; free on Amplemarket, confirmed and
 *              estimated first where the vendor bills per profile shown.
 *   Select   → a checkbox, with the running cost shown as it changes.
 *   Add      → the billed step, confirmed, and it states what it will spend
 *              before it spends it.
 *
 * Nothing enters the system unreviewed, which is why there is no "add all".
 * The per-persona cap and the per-account cap are shown as remaining headroom
 * rather than enforced silently at submit — a refusal after the click teaches
 * nothing, and invites a second run to collect the rest.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, relative } from './api';
import { tip, tipAt } from './tips';
import {
  emptyRunText,
  invalidateVendor,
  revealConfirmText,
  searchConfirmText,
  useVendor,
  type SearchEstimate,
  type VendorDescriptor,
} from './useVendor';

interface Candidate {
  id: number;
  name: string | null;
  title: string | null;
  headline: string | null;
  linkedinUrl: string | null;
  countryCode: string | null;
  companyName: string | null;
  persona: string | null;
  personaFit: number | null;
  personaFitReason: string | null;
  status: string;
  statusReason: string | null;
  email: string | null;
  verificationStatus: string | null;
  verificationDetail: string | null;
  confidence: number | null;
  contactId: number | null;
  source: string;
}

interface RunPayload {
  run: {
    id: number;
    status: string;
    candidatesFound: number;
    revealedCount: number;
    injectedCount: number;
    limitPerPersona: number | null;
    maxContactsPerAccount: number | null;
    createdAt: string;
    reviewedAt: string | null;
  } | null;
  accountRoom?: number;
  contactsHeld?: number;
  candidates?: Candidate[];
  billing?: string;
  /** The vendor THIS run was searched on — inject/reject go through it. With no run, the dial's vendor. */
  vendor?: VendorDescriptor;
  connected?: boolean;
}

export function ProspectPanel({
  accountId,
  available,
  reason,
  note,
  vendor: vendorFromAccount,
  onChanged,
}: {
  accountId: number;
  available: boolean;
  reason: string | null;
  note?: string;
  /** From `GET /accounts/:id` `vendor` when the caller has it; the hook fills in otherwise. */
  vendor?: VendorDescriptor | null;
  onChanged: () => void;
}) {
  const [payload, setPayload] = useState<RunPayload | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);
  /** Kept apart from `message`: a failed load must not read as an empty
      account, and a failed search must not blank a run that is on screen. */
  const [loadError, setLoadError] = useState<string | null>(null);

  // The CURRENT dial's vendor drives Search; the run's own vendor drives Add.
  const hook = useVendor(vendorFromAccount ?? null);
  const searchVendor = hook.vendor;
  const runVendor = payload?.run ? (payload.vendor ?? searchVendor) : searchVendor;

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setPayload(await api<RunPayload>(`/prospect/accounts/${accountId}/latest`));
    } catch (error) {
      /*
       * The failure used to be swallowed whole — `.catch(() => null)` and an
       * `if (result)` that quietly did nothing. `payload` then stayed null, and
       * everything below reads through it with a default: no run, no
       * candidates, no room. So a failed fetch rendered as "this account has
       * never been searched", which is a different and much more expensive
       * thing to tell a rep: the obvious response is to search again, and a
       * search bills the vendor per profile.
       */
      setLoadError(error instanceof ApiError ? error.message : String(error));
    }
  }, [accountId]);

  useEffect(() => {
    if (available) void load();
  }, [available, load]);

  const act = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    setMessage(null);
    try {
      const result = (await fn()) as { note?: string | null };
      await load();
      onChanged();
      setSelected(new Set());
      setMessage({ kind: 'ok', text: result?.note ?? `${label} done.` });
    } catch (e) {
      setMessage({ kind: 'error', text: e instanceof ApiError ? e.message : String(e) });
      // The dial moved under this page: drop the cached descriptor so the next
      // render shows the vendor now in force, and its prices.
      if (e instanceof ApiError && e.code === 'vendor_changed') invalidateVendor();
    } finally {
      setBusy(null);
    }
  };

  /**
   * A billed search is confirmed the way a reveal is. The estimate is free on
   * every vendor that bills searching (people/count), so the number in the
   * confirm is the vendor's own, not a guess — and if the estimate itself
   * fails, nothing is searched: an unknown spend is not a spend to confirm.
   */
  const search = async () => {
    if (!searchVendor) return;
    if (!searchVendor.capabilities.searchIsFree) {
      setBusy('Estimate');
      setMessage(null);
      let estimate: SearchEstimate;
      try {
        estimate = await api<SearchEstimate>(`/prospect/accounts/${accountId}/search-estimate`);
      } catch (e) {
        setMessage({ kind: 'error', text: e instanceof ApiError ? e.message : String(e) });
        setBusy(null);
        return;
      }
      setBusy(null);
      if (!confirm(searchConfirmText(searchVendor, estimate))) return;
    }
    await act('Search', () =>
      api(`/prospect/accounts/${accountId}/search`, {
        method: 'POST',
        // The vendor this page showed a price for. If an admin has since moved
        // the dial, the server refuses rather than billing for a vendor whose
        // cost nobody was shown.
        body: JSON.stringify({ vendor: searchVendor.key }),
      }),
    );
  };

  if (!available) {
    return (
      <section className="drawer-section">
        <h3>Find contacts</h3>
        <p className="fine">{reason}</p>
      </section>
    );
  }

  /*
   * Error before anything derived from a payload that never arrived. Without
   * this the panel below states, in plain words, facts it does not have.
   */
  if (loadError && payload === null) {
    return (
      <section className="drawer-section">
        <h3>Find contacts</h3>
        <p className="error-note">
          Previous searches for this account could not be loaded — {loadError}
        </p>
        <p className="fine">
          Nothing has been searched or billed. Retrying only reads what is already stored.
        </p>
        <div className="draft-actions">
          <button
            type="button"
            {...tip('Asks for this account’s search history again. No search is run and nothing is billed.')}
            onClick={() => void load()}
          >
            Try again
          </button>
        </div>
      </section>
    );
  }

  const run = payload?.run ?? null;
  const candidates = payload?.candidates ?? [];
  const waiting = candidates.filter((c) => c.status === 'preview');
  const decided = candidates.filter((c) => c.status !== 'preview');
  const room = payload?.accountRoom ?? 0;

  const toggle = (id: number) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <section className="drawer-section">
      <h3>Find contacts</h3>
      <p className="why">{note}</p>

      <div className="draft-actions">
        <button
          type="button"
          disabled={busy !== null || !searchVendor}
          {...tipAt(
            searchVendor
              ? `Looks for people at this company matching the titles on your approved personas — on ${searchVendor.displayName} AND in your CRM mirror. ${searchVendor.pricing.search} No addresses are revealed by searching.`
              : 'Finding out which data vendor is in force…',
            'start',
          )}
          onClick={() => void search()}
        >
          {busy === 'Estimate'
            ? 'Estimating…'
            : busy === 'Search'
              ? 'Searching…'
              : !searchVendor
                ? 'Search'
                : run
                  ? `Search ${searchVendor.displayName} again`
                  : `Search ${searchVendor.displayName}`}
        </button>
      </div>

      {run && payload?.vendor && searchVendor && payload.vendor.key !== searchVendor.key && (
        /* The dial moved after this run. Add goes through the run's vendor
           (or is refused by the API if that vendor is gone); say so before
           the click rather than in the refusal. */
        <p className="fine">
          This run was searched on {payload.vendor.displayName}; adding from it reveals through{' '}
          {payload.vendor.displayName}, not {searchVendor.displayName}. Search again to use{' '}
          {searchVendor.displayName}.
        </p>
      )}

      {run && (
        <p className="fine">
          Last run {relative(run.createdAt)} — {run.candidatesFound} found,{' '}
          {run.revealedCount} revealed, {run.injectedCount} added.
          {run.limitPerPersona !== null && ` Reveal limit ${run.limitPerPersona} per persona.`}
        </p>
      )}

      {run && (
        /* Headroom, before the click rather than after. */
        <p className="fine">
          {room > 0
            ? `Room for ${room} more contact${room === 1 ? '' : 's'} at this account (${payload?.contactsHeld} of ${run.maxContactsPerAccount} used).`
            : `This account is at its cap of ${run.maxContactsPerAccount} contacts. Contacting more people at one company is the pattern gate 6 exists to suppress.`}
        </p>
      )}

      {waiting.length > 0 && (
        <>
          {/* The server's own billing sentence for this run's vendor. */}
          <p className="fine">{payload?.billing}</p>

          {waiting.map((candidate) => (
            <label
              key={candidate.id}
              className="candidate-row"
              {...tip(
                'Marks this person for revealing. Selecting is free; nothing is billed until you reveal. Unavailable once this account is at its contact cap.',
              )}
            >
              <input
                type="checkbox"
                checked={selected.has(candidate.id)}
                disabled={room === 0}
                onChange={() => toggle(candidate.id)}
              />
              <span>
                <strong>{candidate.name ?? 'Unnamed'}</strong>
                {candidate.source === 'crm' && <span className="tag">CRM — free</span>}
                {candidate.title && <span className="fine"> · {candidate.title}</span>}
                <span className="fine">
                  {' '}
                  · {candidate.persona}
                  {candidate.personaFit !== null && ` (fit ${candidate.personaFit.toFixed(2)})`}
                </span>
                {candidate.source === 'crm' && candidate.email && (
                  <p className="fine">{candidate.email} — from your CRM, importing costs nothing.</p>
                )}
                {candidate.personaFitReason && (
                  <p className="fine">{candidate.personaFitReason}</p>
                )}
                {/* Flagged before a credit is spent on someone we may hold. */}
                {candidate.statusReason && <p className="fine">{candidate.statusReason}</p>}
                {candidate.linkedinUrl && (
                  <p className="fine">
                    <a
                      href={candidate.linkedinUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      {...tipAt(
                        'Opens this profile on LinkedIn in a new tab. Costs nothing and changes nothing here.',
                        'start',
                      )}
                    >
                      LinkedIn
                    </a>
                  </p>
                )}
              </span>
            </label>
          ))}

          <div className="draft-actions">
            {(() => {
              // CRM-matched selections import for free; only the rest bill.
              const billable = waiting.filter(
                (candidate) => selected.has(candidate.id) && candidate.source !== 'crm',
              ).length;
              const free = selected.size - billable;
              const costLabel =
                billable === 0
                  ? 'free — from your CRM'
                  : `${billable} credit${billable === 1 ? '' : 's'}${free > 0 ? `, ${free} free from CRM` : ''}`;
              return (
                <button
                  type="button"
                  className="primary"
                  disabled={selected.size === 0 || busy !== null || !runVendor}
                  {...tipAt(
                    `Adds the selected people to this account as contacts. ${runVendor?.pricing.reveal ?? ''} Anyone matched in your CRM is imported free with the address the CRM already holds.`,
                    'start',
                  )}
                  onClick={() => {
                    if (!runVendor) return;
                    if (billable === 0 || confirm(revealConfirmText(runVendor, billable, free))) {
                      void act('Add', () =>
                        api(`/prospect/runs/${run!.id}/inject`, {
                          method: 'POST',
                          body: JSON.stringify({ candidateIds: [...selected] }),
                        }),
                      );
                    }
                  }}
                >
                  {/* The cost, in the label. Not in a tooltip. */}
                  {busy === 'Add'
                    ? 'Adding…'
                    : selected.size === 0
                      ? 'Select someone to add'
                      : `Add ${selected.size} (${costLabel})`}
                </button>
              );
            })()}

            {selected.size > 0 && (
              <button
                type="button"
                disabled={busy !== null}
                {...tipAt(
                  'Clears the people you have selected out of this run without revealing them. Nothing is billed, and a later search can bring them back.',
                  'end',
                )}
                onClick={() =>
                  void act('Pass', () =>
                    api(`/prospect/runs/${run!.id}/reject`, {
                      method: 'POST',
                      body: JSON.stringify({ candidateIds: [...selected] }),
                    }),
                  )
                }
              >
                Pass on {selected.size} (free)
              </button>
            )}
          </div>
        </>
      )}

      {run && waiting.length === 0 && candidates.length === 0 && runVendor && (
        <p className="fine">{emptyRunText(runVendor)}</p>
      )}

      {decided.length > 0 && (
        <>
          <h4 className="fine">Already decided</h4>
          {decided.map((candidate) => (
            <p key={candidate.id} className="fine">
              <strong>{candidate.name ?? candidate.email}</strong> — {candidate.status}
              {candidate.email ? ` · ${candidate.email}` : ''}
              {/* The score, not the verdict — a catch-all domain lowers
                  certainty rather than failing the address. */}
              {candidate.confidence !== null &&
                ` · ${Math.round(candidate.confidence * 100)}% confidence`}
              {/* §17.5: a failed verification is not a silent block. */}
              {candidate.verificationDetail && candidate.verificationStatus !== 'verified' && (
                <span> — {candidate.verificationDetail}</span>
              )}
              {candidate.statusReason && <span> {candidate.statusReason}</span>}
            </p>
          ))}
        </>
      )}

      {message && (
        <p className={message.kind === 'error' ? 'error-note' : 'ok-note'}>{message.text}</p>
      )}
    </section>
  );
}
